(() => {
  const core = {
    normalize(value) {
      return String(value || '').trim().toLowerCase();
    },

    findOptionIndex(optionNames, pattern) {
      return optionNames.findIndex((name) => pattern.test(String(name)));
    },

    findVariant(variants, options) {
      return variants.find((candidate) =>
        candidate.options.every((value, optionIndex) => String(value) === String(options[optionIndex]))
      ) || null;
    },

    isFrontBack(printValue) {
      return this.normalize(printValue).includes('back');
    },

    validateFile(file) {
      if (!file) return { error: 'Choose an artwork file.' };
      const extension = String(file.name || '').toLowerCase().split('.').pop();
      const imageExtensions = ['png', 'jpg', 'jpeg', 'webp'];
      const imageTypes = ['image/png', 'image/jpeg', 'image/webp'];
      const isImage = imageTypes.includes(file.type) || imageExtensions.includes(extension);
      const isPDF = file.type === 'application/pdf' || extension === 'pdf';
      if (!isImage && !isPDF) return { error: 'Choose a PNG, JPG, WEBP, or PDF file.' };
      if (file.size > 15 * 1024 * 1024) return { error: 'That file is over 15 MB. Choose a smaller file.' };
      return { error: '', kind: isPDF ? 'pdf' : 'image' };
    },

    validateOrder({ available, size, frontFile, frontText, frontBack, backFile, backText }) {
      if (!available) return 'This shirt combination is currently unavailable.';
      if (!size) return 'Choose a T-shirt size before adding to cart.';
      if (!frontFile && !String(frontText || '').trim()) {
        return 'Add artwork or text to the front design before adding to cart.';
      }
      if (frontBack && !backFile && !String(backText || '').trim()) {
        return 'Add artwork or text to the back design for Front + Back printing.';
      }
      return '';
    },

    constrainPosition({ x, y, imageWidth, imageHeight, zoneWidth, zoneHeight }) {
      if (!zoneWidth || !zoneHeight || !imageWidth || !imageHeight) return { x, y };
      const halfWidth = Math.min(50, (imageWidth / zoneWidth) * 50);
      const halfHeight = Math.min(50, (imageHeight / zoneHeight) * 50);
      return {
        x: Math.min(100 - halfWidth, Math.max(halfWidth, x)),
        y: Math.min(100 - halfHeight, Math.max(halfHeight, y)),
      };
    },

    calculatePrintSize({ imageWidth, imageHeight, zoneWidth, zoneHeight }) {
      if (!zoneWidth || !zoneHeight) return { width: 0, height: 0 };
      return {
        width: Math.min(14, (imageWidth / zoneWidth) * 14),
        height: Math.min(16, (imageHeight / zoneHeight) * 16),
      };
    },

    calculateArtworkBox({ ratio, scale, x, y, zoneWidth, zoneHeight }) {
      const safeRatio = Number(ratio) > 0 ? Number(ratio) : 1;
      const safeScale = Math.min(100, Math.max(1, Number(scale) || 70)) / 100;
      const zoneRatio = zoneWidth / zoneHeight;
      let width;
      let height;
      if (safeRatio >= zoneRatio) {
        width = zoneWidth * safeScale;
        height = width / safeRatio;
      } else {
        height = zoneHeight * safeScale;
        width = height * safeRatio;
      }
      return {
        left: (Number(x) / 100) * zoneWidth - width / 2,
        top: (Number(y) / 100) * zoneHeight - height / 2,
        width,
        height,
      };
    },

    transformText(value, transform) {
      const text = String(value || '');
      return transform === 'uppercase' ? text.toUpperCase() : text;
    },

    formatMoney(cents, currency = 'USD', locale = 'en-US') {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(cents) / 100);
      } catch (error) {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }
    },
  };

  const fontStyles = {
    clean: { label: 'Clean', family: 'Helvetica Neue', fallback: 'Helvetica, Arial, sans-serif', weight: 700 },
    bold: { label: 'Bold', family: 'Arial Black', fallback: '"Helvetica Neue", sans-serif', weight: 900 },
    condensed: { label: 'Condensed', family: 'Bebas Neue', fallback: '"Arial Narrow", sans-serif', weight: 700 },
    college: { label: 'College', family: 'Black Ops One', fallback: 'Rockwell, serif', weight: 700 },
    script: { label: 'Script', family: 'Pacifico', fallback: '"Brush Script MT", cursive', weight: 700 },
    'old-english': { label: 'Old English', family: 'Pirata One', fallback: '"Old English Text MT", serif', weight: 700 },
    graffiti: { label: 'Graffiti', family: 'Permanent Marker', fallback: '"Marker Felt", cursive', weight: 700 },
    retro: { label: 'Retro', family: 'Cooper Black', fallback: 'Georgia, serif', weight: 900 },
    serif: { label: 'Serif', family: 'DM Serif Display', fallback: 'Georgia, serif', weight: 700 },
    typewriter: { label: 'Typewriter', family: 'Special Elite', fallback: '"Courier New", monospace', weight: 700 },
    handwritten: { label: 'Handwritten', family: 'Homemade Apple', fallback: '"Bradley Hand", cursive', weight: 700 },
    street: { label: 'Street', family: 'Impact', fallback: '"Arial Black", sans-serif', weight: 700, style: 'italic', transform: 'uppercase' },
    destroy: { label: 'Destroy', family: 'Rubik Dirt', fallback: 'Impact, sans-serif', weight: 400, transform: 'uppercase' },
    paint: { label: 'Paint', family: 'Rubik Spray Paint', fallback: '"Permanent Marker", cursive', weight: 400, transform: 'uppercase' },
    brush: { label: 'Brush', family: 'Road Rage', fallback: '"Brush Script MT", cursive', weight: 400 },
    dirty: { label: 'Dirty', family: 'Rubik Wet Paint', fallback: 'Impact, sans-serif', weight: 400, transform: 'uppercase' },
  };

  if (typeof window !== 'undefined') {
    window.GroovemanDesignerCore = core;
    window.GroovemanDesignerFonts = fontStyles;
  }
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;

  const tagName = 'grooveman-shirt-designer';
  if (customElements.get(tagName)) return;

  class GroovemanShirtDesigner extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.variants = this.readJSON('[data-gm-variants]', []);
      this.optionNames = this.readJSON('[data-gm-options]', []);
      this.form = this.closest('form');
      this.productRoot = this.closest('.gm-custom-product');
      this.variantInput = this.form?.querySelector('[data-gm-variant-id]');
      this.addButton = this.querySelector('[data-gm-add]');
      this.addButtonText = this.querySelector('[data-gm-add-text]');
      this.formError = this.querySelector('[data-gm-form-error]');
      this.sizeInput = this.querySelector('[data-gm-size]');
      this.quantityInput = this.querySelector('[data-gm-quantity-input]');
      this.uploadZone = this.querySelector('[data-gm-upload-zone]');
      this.uploadTitle = this.querySelector('[data-gm-upload-title]');
      this.fileName = this.querySelector('[data-gm-file-name]');
      this.uploadMessage = this.querySelector('[data-gm-upload-message]');
      this.sideHelp = this.querySelector('[data-gm-side-help]');
      this.artworkPanel = this.querySelector('[data-gm-artwork-panel]');
      this.textPanel = this.querySelector('[data-gm-text-panel]');
      this.artTools = this.querySelector('[data-gm-art-tools]');
      this.scaleInput = this.querySelector('[data-gm-scale]');
      this.scaleOutput = this.querySelector('[data-gm-scale-output]');
      this.inchSize = this.querySelector('[data-gm-inch-size]');
      this.stage = this.querySelector('[data-gm-stage]');
      this.previewLabel = this.querySelector('[data-gm-preview-label]');
      this.unitPrice = this.querySelector('[data-gm-unit-price]');
      this.lineTotal = this.querySelector('[data-gm-line-total]');
      this.savingsBox = this.querySelector('[data-gm-savings]');
      this.savingsAmount = this.querySelector('[data-gm-savings-amount]');
      this.savingsDetails = this.querySelector('[data-gm-savings-details]');
      this.instructions = this.querySelector('.gmcd__instructions textarea');
      this.textInput = this.querySelector('[data-gm-text-input]');
      this.textCount = this.querySelector('[data-gm-text-count]');
      this.textSizeInput = this.querySelector('[data-gm-text-size]');
      this.textSizeOutput = this.querySelector('[data-gm-text-size-output]');
      this.textCustomColor = this.querySelector('[data-gm-text-custom-color]');
      this.downloadButton = this.querySelector('[data-gm-action="download"]');
      this.previewPanels = {
        front: this.querySelector('[data-gm-preview-side="front"]'),
        back: this.querySelector('[data-gm-preview-side="back"]'),
      };
      this.printZones = {
        front: this.querySelector('[data-gm-print-zone="front"]'),
        back: this.querySelector('[data-gm-print-zone="back"]'),
      };
      this.artworkImages = {
        front: this.querySelector('[data-gm-artwork="front"]'),
        back: this.querySelector('[data-gm-artwork="back"]'),
      };
      this.textLayers = {
        front: this.querySelector('[data-gm-text-layer="front"]'),
        back: this.querySelector('[data-gm-text-layer="back"]'),
      };
      this.pdfBadges = {
        front: this.querySelector('[data-gm-pdf="front"]'),
        back: this.querySelector('[data-gm-pdf="back"]'),
      };
      this.emptyStates = {
        front: this.querySelector('[data-gm-empty="front"]'),
        back: this.querySelector('[data-gm-empty="back"]'),
      };
      this.shirts = {
        front: this.querySelector('[data-gm-shirt="front"]'),
        back: this.querySelector('[data-gm-shirt="back"]'),
      };
      this.shirtPaths = {
        front: this.querySelector('[data-gm-shirt-path="front"]'),
        back: this.querySelector('[data-gm-shirt-path="back"]'),
      };
      this.shirtSeams = {
        front: this.querySelector('[data-gm-shirt-seams="front"]'),
        back: this.querySelector('[data-gm-shirt-seams="back"]'),
      };
      this.fileInputs = {
        front: this.querySelector('[data-gm-file="front"]'),
        back: this.querySelector('[data-gm-file="back"]'),
      };
      this.compositeFileInputs = {
        front: this.querySelector('[data-gm-composite-file="front"]'),
        back: this.querySelector('[data-gm-composite-file="back"]'),
      };
      this.metaInputs = {
        front: {
          size: this.querySelector('[data-gm-meta="front-size"]'),
          placement: this.querySelector('[data-gm-meta="front-placement"]'),
        },
        back: {
          size: this.querySelector('[data-gm-meta="back-size"]'),
          placement: this.querySelector('[data-gm-meta="back-placement"]'),
        },
      };
      this.textMetaInputs = {
        front: {
          content: this.querySelector('[data-gm-text-meta="front-content"]'),
          font: this.querySelector('[data-gm-text-meta="front-font"]'),
          color: this.querySelector('[data-gm-text-meta="front-color"]'),
          size: this.querySelector('[data-gm-text-meta="front-size"]'),
          placement: this.querySelector('[data-gm-text-meta="front-placement"]'),
        },
        back: {
          content: this.querySelector('[data-gm-text-meta="back-content"]'),
          font: this.querySelector('[data-gm-text-meta="back-font"]'),
          color: this.querySelector('[data-gm-text-meta="back-color"]'),
          size: this.querySelector('[data-gm-text-meta="back-size"]'),
          placement: this.querySelector('[data-gm-text-meta="back-placement"]'),
        },
      };

      this.indices = {
        color: this.findOptionIndex(/colou?r/i),
        print: this.findOptionIndex(/print/i),
        fit: this.findOptionIndex(/type|fit/i),
      };

      const selectedId = Number(this.dataset.selectedVariantId || this.variantInput?.value);
      this.defaultVariant = this.variants.find((variant) => Number(variant.id) === selectedId) || this.variants[0] || null;
      this.selectedVariant = this.defaultVariant;
      this.state = {
        options: this.defaultVariant?.options ? [...this.defaultVariant.options] : [],
        side: 'front',
        mode: {
          front: 'artwork',
          back: 'artwork',
        },
        art: {
          front: this.newArtState(),
          back: this.newArtState(),
        },
        text: {
          front: this.newTextState(),
          back: this.newTextState(),
        },
      };
      this.drag = null;
      this.textDrag = null;
      this.isPreparingSubmit = false;
      this.compositesPrepared = false;

      this.bindEvents();
      this.updateStartingPrices();
      this.render();
    }

    disconnectedCallback() {
      Object.values(this.state?.art || {}).forEach((item) => this.revokeURL(item));
    }

    readJSON(selector, fallback) {
      try {
        const node = this.querySelector(selector);
        return node ? JSON.parse(node.textContent) : fallback;
      } catch (error) {
        console.error('Grooveman designer data could not be read.', error);
        return fallback;
      }
    }

    findOptionIndex(pattern) {
      return core.findOptionIndex(this.optionNames, pattern);
    }

    newArtState() {
      return {
        file: null,
        name: '',
        kind: '',
        url: '',
        ratio: 1,
        scale: 70,
        x: 50,
        y: 50,
      };
    }

    newTextState() {
      return {
        content: '',
        font: 'clean',
        color: '#ffffff',
        size: 45,
        x: 50,
        y: 50,
      };
    }

    normalize(value) {
      return core.normalize(value);
    }

    currentArt() {
      return this.state.art[this.state.side];
    }

    currentText() {
      return this.state.text[this.state.side];
    }

    currentMode() {
      return this.state.mode[this.state.side];
    }

    hasSideDesign(side) {
      return Boolean(this.state.art[side]?.file || String(this.state.text[side]?.content || '').trim());
    }

    currentFileInput() {
      return this.fileInputs[this.state.side];
    }

    optionValue(role) {
      const index = this.indices[role];
      return index >= 0 ? this.state.options[index] || '' : '';
    }

    isFrontBack() {
      return core.isFrontBack(this.optionValue('print'));
    }

    bindEvents() {
      this.querySelectorAll('[data-option-index]').forEach((button) => {
        button.addEventListener('click', () => this.selectOption(button));
      });

      this.querySelectorAll('[data-gm-side]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!button.disabled) this.setSide(button.dataset.gmSide);
        });
      });

      this.querySelectorAll('[data-gm-content-mode]').forEach((button) => {
        button.addEventListener('click', () => this.setDesignMode(button.dataset.gmContentMode));
      });

      const chooseButton = this.querySelector('[data-gm-action="choose"]');
      chooseButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.chooseFile();
      });
      this.querySelector('[data-gm-action="replace"]')?.addEventListener('click', () => this.chooseFile());
      this.querySelector('[data-gm-action="center"]')?.addEventListener('click', () => this.centerArt());
      this.querySelector('[data-gm-action="remove"]')?.addEventListener('click', () => this.removeArt(this.state.side));
      this.querySelector('[data-gm-action="reset"]')?.addEventListener('click', () => this.resetDesigner());
      this.downloadButton?.addEventListener('click', () => this.downloadCurrentDesign());

      this.uploadZone?.addEventListener('click', (event) => {
        if (!event.target.closest('button')) this.chooseFile();
      });
      this.uploadZone?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.chooseFile();
        }
      });
      ['dragenter', 'dragover'].forEach((eventName) => {
        this.uploadZone?.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.uploadZone.classList.add('is-dragging');
        });
      });
      ['dragleave', 'drop'].forEach((eventName) => {
        this.uploadZone?.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.uploadZone.classList.remove('is-dragging');
        });
      });
      this.uploadZone?.addEventListener('drop', (event) => {
        const file = event.dataTransfer?.files?.[0];
        if (file) this.acceptDroppedFile(file);
      });

      Object.entries(this.fileInputs).forEach(([side, input]) => {
        input?.addEventListener('change', () => {
          const file = input.files?.[0];
          if (file) this.loadFile(side, file);
        });
      });

      this.scaleInput?.addEventListener('input', () => {
        const item = this.currentArt();
        item.scale = Number(this.scaleInput.value);
        this.renderArt();
      });

      this.textInput?.addEventListener('input', () => {
        this.currentText().content = this.textInput.value;
        this.clearFormError();
        this.renderText();
        this.renderSideControls();
        this.renderSummary();
      });

      this.querySelectorAll('[data-gm-font]').forEach((button) => {
        button.addEventListener('click', () => {
          this.currentText().font = button.dataset.gmFont;
          this.loadTextFonts();
          this.renderText();
        });
      });

      this.querySelectorAll('[data-gm-text-color]').forEach((button) => {
        button.addEventListener('click', () => {
          this.currentText().color = button.dataset.gmTextColor;
          if (this.textCustomColor) this.textCustomColor.value = button.dataset.gmTextColor;
          this.renderText();
        });
      });

      this.textCustomColor?.addEventListener('input', () => {
        this.currentText().color = this.textCustomColor.value;
        this.renderText();
      });

      this.textSizeInput?.addEventListener('input', () => {
        this.currentText().size = Number(this.textSizeInput.value);
        this.renderText();
      });

      this.querySelector('[data-gm-action="center-text"]')?.addEventListener('click', () => this.centerText());
      this.querySelector('[data-gm-action="remove-text"]')?.addEventListener('click', () => this.removeText(this.state.side));

      this.sizeInput?.addEventListener('change', () => {
        this.clearFormError();
        this.renderSummary();
      });

      this.querySelectorAll('[data-gm-quantity]').forEach((button) => {
        button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.gmQuantity)));
      });
      this.quantityInput?.addEventListener('input', () => this.renderPrice());
      this.quantityInput?.addEventListener('change', () => {
        this.clampQuantity();
        this.renderPrice();
      });

      Object.entries(this.previewPanels).forEach(([side, panel]) => {
        panel?.addEventListener('click', (event) => {
          if (!event.target.closest('[data-gm-artwork], [data-gm-text-layer]')) this.setSide(side);
        });
        panel?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setSide(side);
          }
        });
      });

      Object.entries(this.artworkImages).forEach(([side, artworkImage]) => {
        artworkImage?.addEventListener('load', () => {
          this.constrainArt(side);
          this.updateArtMeta(side);
        });
        artworkImage?.addEventListener('pointerdown', (event) => this.startDrag(side, event));
        artworkImage?.addEventListener('pointermove', (event) => this.moveDrag(event));
        artworkImage?.addEventListener('pointerup', (event) => this.stopDrag(event));
        artworkImage?.addEventListener('pointercancel', (event) => this.stopDrag(event));
      });

      Object.entries(this.textLayers).forEach(([side, textLayer]) => {
        textLayer?.addEventListener('pointerdown', (event) => this.startTextDrag(side, event));
        textLayer?.addEventListener('pointermove', (event) => this.moveTextDrag(event));
        textLayer?.addEventListener('pointerup', (event) => this.stopTextDrag(event));
        textLayer?.addEventListener('pointercancel', (event) => this.stopTextDrag(event));
        textLayer?.addEventListener('keydown', (event) => this.moveTextWithKeyboard(side, event));
      });

      window.addEventListener('resize', () => {
        this.renderArt();
        this.renderText();
      }, { passive: true });
      this.form?.addEventListener('submit', (event) => this.validateSubmit(event), true);
    }

    selectOption(button) {
      const index = Number(button.dataset.optionIndex);
      const wasFrontBack = this.isFrontBack();
      const options = [...this.state.options];
      options[index] = button.dataset.value;
      const variant = core.findVariant(this.variants, options);

      if (!variant) {
        this.showFormError('That combination is not available. Please choose another option.');
        return;
      }

      this.state.options = options;
      this.selectedVariant = variant;
      if (this.variantInput) this.variantInput.value = variant.id;
      const frontBack = this.isFrontBack();
      if (!frontBack && this.state.side === 'back') this.state.side = 'front';
      if (!wasFrontBack && frontBack && this.hasSideDesign('front') && !this.hasSideDesign('back')) {
        this.state.side = 'back';
        this.showUploadMessage('Front design is ready. Now add artwork, text, or both to the back.');
      }
      this.clearFormError();
      this.render();
    }

    setSide(side) {
      if (side === 'back' && !this.isFrontBack()) return;
      this.state.side = side;
      this.showUploadMessage('');
      this.render();
    }

    setDesignMode(mode) {
      if (!['artwork', 'text'].includes(mode)) return;
      this.state.mode[this.state.side] = mode;
      if (mode === 'text') this.loadTextFonts();
      this.showUploadMessage('');
      this.renderDesignMode();
      this.renderTextControls();
      if (mode === 'text') this.textInput?.focus();
    }

    loadTextFonts() {
      if (document.querySelector('[data-gm-designer-fonts]')) return;
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Black+Ops+One&family=DM+Serif+Display&family=Homemade+Apple&family=Pacifico&family=Permanent+Marker&family=Pirata+One&family=Road+Rage&family=Rubik+Dirt&family=Rubik+Spray+Paint&family=Rubik+Wet+Paint&family=Special+Elite&display=swap';
      fontLink.dataset.gmDesignerFonts = 'true';
      document.head.appendChild(fontLink);
    }

    chooseFile() {
      this.currentFileInput()?.click();
    }

    acceptDroppedFile(file) {
      const input = this.currentFileInput();
      if (!input) return;
      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
      } catch (error) {
        this.showUploadMessage('Please use Choose File to add this artwork in your browser.', true);
        return;
      }
      this.loadFile(this.state.side, file);
    }

    validateFile(file) {
      return core.validateFile(file);
    }

    loadFile(side, file) {
      const validation = this.validateFile(file);
      if (validation.error) {
        if (this.fileInputs[side]) this.fileInputs[side].value = '';
        this.showUploadMessage(validation.error, true);
        return;
      }

      const item = this.state.art[side];
      this.revokeURL(item);
      Object.assign(item, {
        file,
        name: file.name,
        kind: validation.kind,
        url: '',
        ratio: 1,
        scale: 70,
        x: 50,
        y: 50,
      });

      if (validation.kind === 'image') {
        item.url = URL.createObjectURL(file);
        const probe = new Image();
        probe.onload = () => {
          item.ratio = probe.naturalWidth / probe.naturalHeight || 1;
          this.renderPreviewArt(side);
        };
        probe.onerror = () => {
          this.removeArt(side, false);
          this.showUploadMessage('That image could not be previewed. Please try another file.', true);
        };
        probe.src = item.url;
      }

      const needsBackDesign = side === 'front' && this.isFrontBack() && !this.hasSideDesign('back');
      this.state.side = needsBackDesign ? 'back' : side;
      this.showUploadMessage(
        needsBackDesign
          ? `${file.name} added to the front. Now add the back design.`
          : `${file.name} added to the ${side}.`
      );
      this.clearFormError();
      this.render();
    }

    revokeURL(item) {
      if (item?.url?.startsWith('blob:')) URL.revokeObjectURL(item.url);
    }

    removeArt(side, announce = true) {
      const oldItem = this.state.art[side];
      this.revokeURL(oldItem);
      this.state.art[side] = this.newArtState();
      if (this.fileInputs[side]) this.fileInputs[side].value = '';
      if (announce) this.showUploadMessage(`${side === 'front' ? 'Front' : 'Back'} artwork removed.`);
      this.render();
    }

    centerArt() {
      const item = this.currentArt();
      item.x = 50;
      item.y = 50;
      this.renderArt();
    }

    removeText(side, announce = true) {
      this.state.text[side] = this.newTextState();
      if (announce) this.showUploadMessage(`${side === 'front' ? 'Front' : 'Back'} text removed.`);
      this.render();
    }

    centerText() {
      const item = this.currentText();
      item.x = 50;
      item.y = 50;
      this.renderText();
    }

    resetDesigner() {
      this.removeArt('front', false);
      this.removeArt('back', false);
      this.state.text.front = this.newTextState();
      this.state.text.back = this.newTextState();
      this.state.mode.front = 'artwork';
      this.state.mode.back = 'artwork';
      this.clearCompositeFile('front');
      this.clearCompositeFile('back');
      this.compositesPrepared = false;
      this.selectedVariant = this.defaultVariant;
      this.state.options = this.defaultVariant?.options ? [...this.defaultVariant.options] : [];
      this.state.side = 'front';
      if (this.variantInput && this.defaultVariant) this.variantInput.value = this.defaultVariant.id;
      if (this.sizeInput) this.sizeInput.value = '';
      if (this.quantityInput) this.quantityInput.value = this.quantityInput.min || 1;
      if (this.instructions) this.instructions.value = '';
      this.showUploadMessage('Designer reset.');
      this.clearFormError();
      this.render();
    }

    changeQuantity(delta) {
      const current = Number(this.quantityInput?.value) || 1;
      if (this.quantityInput) this.quantityInput.value = current + delta;
      this.clampQuantity();
      this.renderPrice();
    }

    clampQuantity() {
      if (!this.quantityInput) return 1;
      const min = Number(this.quantityInput.min) || 1;
      const max = Number(this.quantityInput.max) || 99;
      const value = Math.min(max, Math.max(min, Number(this.quantityInput.value) || min));
      this.quantityInput.value = value;
      return value;
    }

    render() {
      this.renderOptions();
      this.renderSideControls();
      this.renderShirt();
      this.renderDesignMode();
      this.renderArt();
      this.renderText();
      this.renderSummary();
      this.renderPrice();
      this.renderAvailability();
    }

    renderOptions() {
      this.querySelectorAll('[data-option-index]').forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const active = String(this.state.options[index]) === String(button.dataset.value);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    renderSideControls() {
      const frontBack = this.isFrontBack();
      this.querySelectorAll('[data-gm-side]').forEach((button) => {
        const side = button.dataset.gmSide;
        const active = button.dataset.gmSide === this.state.side;
        const hasDesign = this.hasSideDesign(side);
        const hasArt = Boolean(this.state.art[side]?.file);
        const hasText = Boolean(String(this.state.text[side]?.content || '').trim());
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-complete', hasDesign);
        button.classList.toggle('is-required', !hasDesign && (side === 'front' || frontBack));
        button.setAttribute('aria-pressed', String(active));
        if (side === 'back') button.disabled = !frontBack;

        const status = button.querySelector(`[data-gm-side-status="${side}"]`);
        if (status) {
          status.textContent = side === 'back' && !frontBack
            ? 'SELECT FRONT + BACK'
            : hasArt && hasText
              ? 'ART + TEXT ✓'
              : hasArt
                ? 'ARTWORK ✓'
                : hasText
                  ? 'TEXT ✓'
                  : 'DESIGN REQUIRED';
        }
      });

      if (this.sideHelp) this.sideHelp.hidden = !frontBack;
      if (this.fileInputs.back) this.fileInputs.back.disabled = !frontBack;
      Object.values(this.metaInputs.back).forEach((input) => {
        if (input) input.disabled = !frontBack;
      });
      this.stage?.classList.toggle('is-front-back', frontBack);

      Object.entries(this.previewPanels).forEach(([side, panel]) => {
        const active = side === this.state.side;
        const hasDesign = this.hasSideDesign(side);
        panel?.classList.toggle('is-active', active);
        panel?.setAttribute('aria-pressed', String(active));
        const status = panel?.querySelector(`[data-gm-preview-status="${side}"]`);
        if (status) status.textContent = hasDesign ? 'DESIGN READY ✓' : 'ADD DESIGN';
      });
    }

    renderDesignMode() {
      const mode = this.currentMode();
      this.querySelectorAll('[data-gm-content-mode]').forEach((button) => {
        const active = button.dataset.gmContentMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      if (this.artworkPanel) this.artworkPanel.hidden = mode !== 'artwork';
      if (this.textPanel) this.textPanel.hidden = mode !== 'text';
      Object.entries(this.printZones).forEach(([side, printZone]) => {
        const sideMode = this.state.mode[side];
        printZone?.classList.toggle('is-editing-artwork', sideMode === 'artwork');
        printZone?.classList.toggle('is-editing-text', sideMode === 'text');
      });
    }

    renderShirt() {
      const color = this.optionValue('color') || 'Black';
      const white = this.normalize(color).includes('white');
      const collarColor = white ? '#d1d1ce' : '#050505';
      Object.entries(this.shirtPaths).forEach(([side, shirtPath]) => {
        const fillId = white ? shirtPath?.dataset.gmWhiteFill : shirtPath?.dataset.gmBlackFill;
        if (shirtPath && fillId) shirtPath.setAttribute('fill', `url(#${fillId})`);
        this.shirtSeams[side]?.setAttribute('stroke', white ? 'rgba(0,0,0,.08)' : 'rgba(255,255,255,.08)');
        this.shirts[side]?.setAttribute('aria-label', `${color} T-shirt ${side} preview`);
      });
      this.querySelector('[data-gm-collar="front"]')?.setAttribute('fill', collarColor);
      this.querySelector('[data-gm-collar="back"]')?.setAttribute('stroke', collarColor);
    }

    renderArt() {
      const item = this.currentArt();
      const sideLabel = this.state.side === 'front' ? 'FRONT' : 'BACK';
      if (this.uploadTitle) this.uploadTitle.textContent = `UPLOAD ${sideLabel} ARTWORK`;
      this.uploadZone?.setAttribute('aria-label', `Upload ${this.state.side} artwork`);
      if (this.fileName) {
       this.fileName.textContent = item.name || 'PNG preferred · JPG/PDF accepted · High resolution · Max 15 MB';
        this.fileName.title = item.name || '';
      }

      const hasEditableImage = item.kind === 'image' && item.url;
      if (this.artTools) this.artTools.hidden = !hasEditableImage;
      if (hasEditableImage) {
        if (this.scaleInput) this.scaleInput.value = item.scale;
        if (this.scaleOutput) this.scaleOutput.textContent = `${item.scale}%`;
      }

      this.renderPreviewArt('front');
      this.renderPreviewArt('back');
    }

    renderPreviewArt(side) {
      const item = this.state.art[side];
      const artworkImage = this.artworkImages[side];
      const emptyState = this.emptyStates[side];
      const printZone = this.printZones[side];
      const pdfBadge = this.pdfBadges[side];
      const hasText = Boolean(String(this.state.text[side]?.content || '').trim());
      if (!item || !artworkImage || !emptyState || !printZone) return;

      const emptySymbol = emptyState.querySelector('b');
      const emptyText = emptyState.querySelector('small');
      if (item.kind === 'image' && item.url) {
        if (artworkImage.src !== item.url) artworkImage.src = item.url;
        artworkImage.hidden = false;
        if (pdfBadge) pdfBadge.hidden = true;
        emptyState.hidden = true;
        const zoneRatio = printZone.clientWidth && printZone.clientHeight
          ? printZone.clientWidth / printZone.clientHeight
          : 14 / 16;
        if (item.ratio >= zoneRatio) {
          artworkImage.style.width = `${item.scale}%`;
          artworkImage.style.height = 'auto';
        } else {
          artworkImage.style.width = 'auto';
          artworkImage.style.height = `${item.scale}%`;
        }
        artworkImage.style.left = `${item.x}%`;
        artworkImage.style.top = `${item.y}%`;
        requestAnimationFrame(() => {
          this.constrainArt(side);
          this.updateArtMeta(side);
        });
        return;
      }

      artworkImage.hidden = true;
      artworkImage.removeAttribute('src');
      if (pdfBadge) pdfBadge.hidden = item.kind !== 'pdf';
      emptyState.hidden = Boolean(item.file || hasText);
      if (item.kind === 'pdf') {
        this.setMeta(side, 'PDF file — confirm placement', 'Centered by default');
      } else {
        if (emptySymbol) emptySymbol.textContent = '+';
        if (emptyText) emptyText.innerHTML = `${side.toUpperCase()} DESIGN<br>GOES HERE`;
        this.setMeta(side, '', '');
      }
    }

    renderText() {
      this.renderTextControls();
      this.renderPreviewText('front');
      this.renderPreviewText('back');
    }

    renderTextControls() {
      const item = this.currentText();
      if (this.textInput && this.textInput.value !== item.content) this.textInput.value = item.content;
      if (this.textCount) this.textCount.textContent = String(item.content.length);
      if (this.textSizeInput) this.textSizeInput.value = item.size;
      if (this.textSizeOutput) this.textSizeOutput.textContent = `${item.size}%`;
      if (this.textCustomColor) this.textCustomColor.value = item.color;

      this.querySelectorAll('[data-gm-font]').forEach((button) => {
        const active = button.dataset.gmFont === item.font;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      this.querySelectorAll('[data-gm-text-color]').forEach((button) => {
        const active = this.normalize(button.dataset.gmTextColor) === this.normalize(item.color);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    renderPreviewText(side) {
      const item = this.state.text[side];
      const textLayer = this.textLayers[side];
      const printZone = this.printZones[side];
      const emptyState = this.emptyStates[side];
      if (!item || !textLayer || !printZone || !emptyState) return;

      const content = String(item.content || '').trim();
      if (!content) {
        textLayer.hidden = true;
        textLayer.textContent = '';
        emptyState.hidden = this.hasSideDesign(side);
        this.updateTextMeta(side);
        return;
      }

      textLayer.textContent = item.content;
      textLayer.hidden = false;
      textLayer.dataset.gmFontStyle = item.font;
      textLayer.style.color = item.color;
      textLayer.style.fontSize = `${Math.max(10, (item.size / 100) * printZone.clientWidth * 0.34)}px`;
      textLayer.style.left = `${item.x}%`;
      textLayer.style.top = `${item.y}%`;
      emptyState.hidden = true;

      requestAnimationFrame(() => {
        this.constrainText(side);
        this.updateTextMeta(side);
      });
    }

    constrainText(side = this.state.side) {
      const item = this.state.text[side];
      const textLayer = this.textLayers[side];
      const printZone = this.printZones[side];
      if (!item || !textLayer || !printZone || textLayer.hidden) return;
      const zoneRect = printZone.getBoundingClientRect();
      const textRect = textLayer.getBoundingClientRect();
      if (!zoneRect.width || !zoneRect.height || !textRect.width || !textRect.height) return;
      const constrained = core.constrainPosition({
        x: item.x,
        y: item.y,
        imageWidth: textRect.width,
        imageHeight: textRect.height,
        zoneWidth: zoneRect.width,
        zoneHeight: zoneRect.height,
      });
      item.x = constrained.x;
      item.y = constrained.y;
      textLayer.style.left = `${item.x}%`;
      textLayer.style.top = `${item.y}%`;
    }

    updateTextMeta(side = this.state.side) {
      const item = this.state.text[side];
      const inputs = this.textMetaInputs[side];
      if (!item || !inputs) return;
      const content = String(item.content || '').trim();
      const enabled = Boolean(content) && (side === 'front' || this.isFrontBack());
      const values = {
        content,
        font: fontStyles[item.font]?.label || item.font,
        color: String(item.color || '').toUpperCase(),
        size: `${item.size}%`,
        placement: `${item.x.toFixed(0)}% from left × ${item.y.toFixed(0)}% from top`,
      };
      Object.entries(inputs).forEach(([key, input]) => {
        if (!input) return;
        input.value = enabled ? values[key] : '';
        input.disabled = !enabled;
      });
    }

    constrainArt(side = this.state.side) {
      const item = this.state.art[side];
      const artworkImage = this.artworkImages[side];
      const printZone = this.printZones[side];
      if (!item || !artworkImage || !printZone || item.kind !== 'image' || artworkImage.hidden) return;
      const zoneRect = printZone.getBoundingClientRect();
      const imageRect = artworkImage.getBoundingClientRect();
      if (!zoneRect.width || !zoneRect.height || !imageRect.width || !imageRect.height) return;
      const constrained = core.constrainPosition({
        x: item.x,
        y: item.y,
        imageWidth: imageRect.width,
        imageHeight: imageRect.height,
        zoneWidth: zoneRect.width,
        zoneHeight: zoneRect.height,
      });
      item.x = constrained.x;
      item.y = constrained.y;
      artworkImage.style.left = `${item.x}%`;
      artworkImage.style.top = `${item.y}%`;
    }

    updateArtMeta(side = this.state.side) {
      const item = this.state.art[side];
      const artworkImage = this.artworkImages[side];
      const printZone = this.printZones[side];
      if (!item || !artworkImage || !printZone || item.kind !== 'image' || artworkImage.hidden) return;
      const zoneRect = printZone.getBoundingClientRect();
      const imageRect = artworkImage.getBoundingClientRect();
      if (!zoneRect.width || !zoneRect.height) return;
      const dimensions = core.calculatePrintSize({
        imageWidth: imageRect.width,
        imageHeight: imageRect.height,
        zoneWidth: zoneRect.width,
        zoneHeight: zoneRect.height,
      });
      const width = dimensions.width;
      const height = dimensions.height;
      const size = `${width.toFixed(1)}\" W × ${height.toFixed(1)}\" H`;
      const placement = `${item.x.toFixed(0)}% from left × ${item.y.toFixed(0)}% from top`;
      if (this.inchSize && side === this.state.side) this.inchSize.textContent = `Approx. print size: ${size}`;
      this.setMeta(side, size, placement);
    }

    setMeta(side, size, placement) {
      if (this.metaInputs[side]?.size) this.metaInputs[side].size.value = size;
      if (this.metaInputs[side]?.placement) this.metaInputs[side].placement.value = placement;
    }

    startDrag(side, event) {
      if (side === 'back' && !this.isFrontBack()) return;
      this.state.mode[side] = 'artwork';
      if (this.state.side !== side) {
        this.state.side = side;
        this.showUploadMessage('');
        this.renderSideControls();
        this.renderDesignMode();
        this.renderArt();
        this.renderTextControls();
        this.renderSummary();
      }
      const item = this.state.art[side];
      const artworkImage = this.artworkImages[side];
      if (!item.url) return;
      event.preventDefault();
      artworkImage.setPointerCapture?.(event.pointerId);
      this.drag = {
        id: event.pointerId,
        side,
        startX: event.clientX,
        startY: event.clientY,
        x: item.x,
        y: item.y,
      };
      artworkImage.classList.add('is-dragging');
    }

    moveDrag(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const side = this.drag.side;
      const printZone = this.printZones[side];
      const artworkImage = this.artworkImages[side];
      const rect = printZone.getBoundingClientRect();
      const item = this.state.art[side];
      item.x = this.drag.x + ((event.clientX - this.drag.startX) / rect.width) * 100;
      item.y = this.drag.y + ((event.clientY - this.drag.startY) / rect.height) * 100;
      artworkImage.style.left = `${item.x}%`;
      artworkImage.style.top = `${item.y}%`;
      this.constrainArt(side);
      this.updateArtMeta(side);
    }

    stopDrag(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const side = this.drag.side;
      const artworkImage = this.artworkImages[side];
      artworkImage.releasePointerCapture?.(event.pointerId);
      this.drag = null;
      artworkImage.classList.remove('is-dragging');
      this.updateArtMeta(side);
    }

    startTextDrag(side, event) {
      if (side === 'back' && !this.isFrontBack()) return;
      const item = this.state.text[side];
      const textLayer = this.textLayers[side];
      if (!String(item?.content || '').trim() || !textLayer) return;
      event.preventDefault();
      event.stopPropagation();
      this.state.side = side;
      this.state.mode[side] = 'text';
      this.renderSideControls();
      this.renderDesignMode();
      this.renderTextControls();
      this.renderSummary();
      textLayer.setPointerCapture?.(event.pointerId);
      this.textDrag = {
        id: event.pointerId,
        side,
        startX: event.clientX,
        startY: event.clientY,
        x: item.x,
        y: item.y,
      };
      textLayer.classList.add('is-dragging');
    }

    moveTextDrag(event) {
      if (!this.textDrag || this.textDrag.id !== event.pointerId) return;
      const side = this.textDrag.side;
      const printZone = this.printZones[side];
      const textLayer = this.textLayers[side];
      const item = this.state.text[side];
      const rect = printZone.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      item.x = this.textDrag.x + ((event.clientX - this.textDrag.startX) / rect.width) * 100;
      item.y = this.textDrag.y + ((event.clientY - this.textDrag.startY) / rect.height) * 100;
      textLayer.style.left = `${item.x}%`;
      textLayer.style.top = `${item.y}%`;
      this.constrainText(side);
      this.updateTextMeta(side);
    }

    stopTextDrag(event) {
      if (!this.textDrag || this.textDrag.id !== event.pointerId) return;
      const side = this.textDrag.side;
      const textLayer = this.textLayers[side];
      textLayer.releasePointerCapture?.(event.pointerId);
      this.textDrag = null;
      textLayer.classList.remove('is-dragging');
      this.updateTextMeta(side);
    }

    moveTextWithKeyboard(side, event) {
      const movement = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      }[event.key];
      if (!movement || (side === 'back' && !this.isFrontBack())) return;
      event.preventDefault();
      event.stopPropagation();
      const item = this.state.text[side];
      const step = event.shiftKey ? 5 : 1;
      item.x += movement[0] * step;
      item.y += movement[1] * step;
      this.state.side = side;
      this.state.mode[side] = 'text';
      this.render();
      this.textLayers[side]?.focus({ preventScroll: true });
    }

    fontDescriptor(fontKey, size) {
      const style = fontStyles[fontKey] || fontStyles.clean;
      const fontStyle = style.style || 'normal';
      const family = `"${style.family}", ${style.fallback}`;
      return {
        css: `${fontStyle} ${style.weight || 700} ${size}px ${family}`,
        load: `${fontStyle} ${style.weight || 700} ${size}px "${style.family}"`,
        style,
      };
    }

    async waitForFont(fontKey, size = 64) {
      this.loadTextFonts();
      if (!document.fonts?.load) return;
      const descriptor = this.fontDescriptor(fontKey, size);
      await Promise.race([
        document.fonts.load(descriptor.load),
        new Promise((resolve) => window.setTimeout(resolve, 2500)),
      ]).catch(() => {});
    }

    wrapCanvasText(context, text, maxWidth) {
      const lines = [];
      const breakToken = (token) => {
        let part = '';
        Array.from(token).forEach((character) => {
          const next = part + character;
          if (part && context.measureText(next).width > maxWidth) {
            lines.push(part);
            part = character;
          } else {
            part = next;
          }
        });
        return part;
      };

      String(text || '').split('\n').forEach((paragraph) => {
        if (!paragraph) {
          lines.push('');
          return;
        }
        const words = paragraph.split(/\s+/);
        let line = '';
        words.forEach((word) => {
          const candidate = line ? `${line} ${word}` : word;
          if (context.measureText(candidate).width <= maxWidth) {
            line = candidate;
            return;
          }
          if (line) lines.push(line);
          if (context.measureText(word).width > maxWidth) {
            line = breakToken(word);
          } else {
            line = word;
          }
        });
        lines.push(line);
      });
      return lines.length ? lines : [''];
    }

    async drawableArtwork(side) {
      const item = this.state.art[side];
      const image = this.artworkImages[side];
      if (item?.kind !== 'image' || !item.url || !image) return null;
      if (image.complete && !image.naturalWidth) throw new Error('The artwork could not be prepared.');
      if (!image.complete || !image.naturalWidth) {
        await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => {
            cleanup();
            reject(new Error('The artwork took too long to prepare. Please try again.'));
          }, 5000);
          const cleanup = () => {
            window.clearTimeout(timeout);
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
          };
          const onLoad = () => {
            cleanup();
            resolve();
          };
          const onError = () => {
            cleanup();
            reject(new Error('The artwork could not be prepared.'));
          };
          image.addEventListener('load', onLoad, { once: true });
          image.addEventListener('error', onError, { once: true });
        });
      }
      if (image.decode) {
        await Promise.race([
          image.decode().catch(() => {}),
          new Promise((resolve) => window.setTimeout(resolve, 2500)),
        ]);
      }
      return image;
    }

    async buildCompositeBlob(side) {
      const art = this.state.art[side];
      const text = this.state.text[side];
      const hasImage = art?.kind === 'image' && art.url;
      const hasText = Boolean(String(text?.content || '').trim());
      if (!hasImage && !hasText) throw new Error(`Add artwork or text to the ${side} design first.`);
      if (art?.kind === 'pdf') {
        throw new Error('PDF artwork cannot be merged in the browser. Use PNG, JPG, or WEBP when combining artwork with text.');
      }

      const width = 1750;
      const height = 2000;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) throw new Error('This browser could not prepare the design image.');
      context.clearRect(0, 0, width, height);
      context.save();
      context.beginPath();
      context.rect(0, 0, width, height);
      context.clip();

      if (hasImage) {
        const image = await this.drawableArtwork(side);
        if (!image) throw new Error('The artwork could not be prepared.');
        const box = core.calculateArtworkBox({
          ratio: art.ratio,
          scale: art.scale,
          x: art.x,
          y: art.y,
          zoneWidth: width,
          zoneHeight: height,
        });
        context.drawImage(image, box.left, box.top, box.width, box.height);
      }

      if (hasText) {
        const fontSize = Math.max(10, (text.size / 100) * width * 0.34);
        await this.waitForFont(text.font, Math.round(fontSize));
        const descriptor = this.fontDescriptor(text.font, fontSize);
        const content = core.transformText(text.content, descriptor.style.transform);
        context.font = descriptor.css;
        context.fillStyle = text.color || '#ffffff';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        const lines = this.wrapCanvasText(context, content, width * 0.96);
        const lineHeight = fontSize * 1.03;
        const centerX = (text.x / 100) * width;
        const centerY = (text.y / 100) * height;
        lines.forEach((line, index) => {
          const y = centerY + (index - (lines.length - 1) / 2) * lineHeight;
          context.fillText(line, centerX, y, width * 0.96);
        });
      }

      context.restore();
      let blob;
      try {
        blob = await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(
            () => reject(new Error('The design image took too long to create. Please try again.')),
            7000
          );
          canvas.toBlob((result) => {
            window.clearTimeout(timeout);
            resolve(result);
          }, 'image/png');
        });
      } finally {
        canvas.width = 1;
        canvas.height = 1;
      }
      if (!blob) throw new Error('The design image could not be created. Please try again.');
      return blob;
    }

    clearCompositeFile(side) {
      const input = this.compositeFileInputs[side];
      if (!input) return;
      input.value = '';
      input.disabled = true;
    }

    setCompositeFile(side, blob) {
      const input = this.compositeFileInputs[side];
      if (!input || typeof DataTransfer === 'undefined' || typeof File === 'undefined') {
        throw new Error('This browser cannot attach the combined design. Please try Safari, Chrome, or Edge updated to the latest version.');
      }
      const transfer = new DataTransfer();
      const file = new File([blob], `grooveman-${side}-final-design.png`, { type: 'image/png' });
      transfer.items.add(file);
      input.files = transfer.files;
      input.disabled = false;
    }

    async prepareCompositeFiles() {
      const sides = this.isFrontBack() ? ['front', 'back'] : ['front'];
      this.clearCompositeFile('front');
      this.clearCompositeFile('back');
      for (const side of sides) {
        const art = this.state.art[side];
        if (art?.kind === 'pdf') continue;
        const blob = await this.buildCompositeBlob(side);
        this.setCompositeFile(side, blob);
      }
    }

    async downloadCurrentDesign() {
      if (!this.downloadButton || this.downloadButton.disabled) return;
      const side = this.state.side;
      const originalLabel = this.downloadButton.textContent;
      this.downloadButton.disabled = true;
      this.downloadButton.textContent = 'PREPARING…';
      this.showUploadMessage('Preparing artwork and text…');
      try {
        const blob = await this.buildCompositeBlob(side);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `grooveman-${side}-final-design.png`;
        link.rel = 'noopener';
        link.hidden = true;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 2000);
        this.showUploadMessage(`${side === 'front' ? 'Front' : 'Back'} design downloaded with artwork and text combined.`);
      } catch (error) {
        this.showUploadMessage(error?.message || 'The design could not be downloaded. Please try again.', true);
      } finally {
        this.downloadButton.disabled = false;
        this.downloadButton.textContent = originalLabel;
      }
    }

    renderSummary() {
      this.setSummary('fit', this.optionValue('fit') || '—');
      this.setSummary('color', this.optionValue('color') || '—');
      this.setSummary('printing', this.optionValue('print') || '—');
      this.setSummary('size', this.sizeInput?.value || 'Not selected');
      this.setSummary('design', this.designStatus());
      const fit = this.optionValue('fit');
      const color = this.optionValue('color');
      const previewMode = this.isFrontBack() ? 'FRONT + BACK' : 'FRONT';
      const parts = [fit, color, previewMode].filter(Boolean).map((value) => String(value).toUpperCase());
      if (this.previewLabel) this.previewLabel.textContent = parts.join(' / ');
      if (this.downloadButton && !this.downloadButton.disabled) {
        this.downloadButton.textContent = `DOWNLOAD ${this.state.side.toUpperCase()}`;
      }
    }

    setSummary(key, value) {
      const node = this.querySelector(`[data-gm-summary="${key}"]`);
      if (node) {
        node.textContent = value;
        node.title = value;
      }
    }

    designStatus() {
      const front = this.hasSideDesign('front');
      const back = this.hasSideDesign('back');
      if (!this.isFrontBack()) return front ? this.describeSideDesign('front') : 'Not added';
      if (front && back) return 'Front + back ready';
      if (front) return 'Back design needed';
      if (back) return 'Front design needed';
      return 'Not added';
    }

    describeSideDesign(side) {
      const hasArt = Boolean(this.state.art[side]?.file);
      const hasText = Boolean(String(this.state.text[side]?.content || '').trim());
      if (hasArt && hasText) return 'Artwork + text ready';
      if (hasArt) return this.state.art[side].name || 'Artwork ready';
      if (hasText) return 'Text ready';
      return 'Not added';
    }

    renderPrice() {
  const price = Number(this.selectedVariant?.price) || 0;
  const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
  const regularTotal = price * quantity;

  let discount = 0;
  let offer = '';

  if (quantity >= 48) {
    discount = regularTotal * 0.35;
    offer = '35% OFF · 48+ T-SHIRTS';
  } else if (quantity >= 24) {
    discount = regularTotal * 0.30;
    offer = '30% OFF · 24+ T-SHIRTS';
  } else if (quantity >= 12) {
    discount = regularTotal * 0.25;
    offer = '25% OFF · 12+ T-SHIRTS';
  } else if (quantity >= 3) {
    discount = Math.floor(quantity / 3) * price * 0.50;
    offer = 'BUY 2, GET THE 3RD 50% OFF';
  }

  const savings = Math.round(discount);
  const discountedTotal = regularTotal - savings;

  const discountedUnit = discountedTotal / quantity;
  const unit = this.formatMoney(discountedUnit);
  const originalUnit = this.formatMoney(price);
  const total = this.formatMoney(regularTotal);

  if (this.unitPrice) {
  if (savings > 0) {
    this.unitPrice.innerHTML =
      `<span style="font-size:0.55em;color:#999;text-decoration:line-through;margin-right:8px;">${originalUnit}</span>` +
    `<span style="font-size:30px;font-weight:900;color:#DFFF00;">${unit}</span>`;
  } else {
    this.unitPrice.textContent = unit;
  }
}
  if (this.lineTotal) this.lineTotal.textContent = this.formatMoney(discountedTotal);

  if (this.savingsBox && this.savingsAmount && this.savingsDetails) {
    this.savingsBox.hidden = savings <= 0;
    if (savings > 0) {
      this.savingsAmount.textContent = `🔥 YOU SAVE ${this.formatMoney(savings)}!`;
      this.savingsDetails.textContent =
        `${offer} · Estimated total ${this.formatMoney(discountedTotal)} · Discount confirmed in cart`;
    }
  }

  if (this.addButtonText && this.selectedVariant?.available) {
    this.addButtonText.textContent = `ADD TO CART · ${this.formatMoney(discountedTotal)}`;
  }
}

    renderAvailability() {
      const available = Boolean(this.selectedVariant?.available);
      if (this.addButton) this.addButton.disabled = !available;
      if (this.addButtonText && !available) this.addButtonText.textContent = 'SOLD OUT';
      if (this.variantInput && this.selectedVariant) this.variantInput.value = this.selectedVariant.id;
    }

    updateStartingPrices() {
      const printIndex = this.indices.print;
      if (printIndex < 0) return;
      const front = this.variants.filter((variant) => !this.normalize(variant.options[printIndex]).includes('back'));
      const back = this.variants.filter((variant) => this.normalize(variant.options[printIndex]).includes('back'));
      const frontPrice = front.length ? Math.min(...front.map((variant) => Number(variant.price))) : null;
      const backPrice = back.length ? Math.min(...back.map((variant) => Number(variant.price))) : null;
      const frontOutput = this.productRoot?.querySelector('[data-gm-front-start]');
      const backOutput = this.productRoot?.querySelector('[data-gm-front-back-start]');
      if (frontOutput && Number.isFinite(frontPrice)) frontOutput.textContent = this.formatMoney(frontPrice);
      if (backOutput && Number.isFinite(backPrice)) backOutput.textContent = this.formatMoney(backPrice);
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      const locale = this.dataset.locale || document.documentElement.lang || 'en-US';
      return core.formatMoney(cents, currency, locale);
    }

    showUploadMessage(message, error = false) {
      if (!this.uploadMessage) return;
      this.uploadMessage.textContent = message;
      this.uploadMessage.classList.toggle('is-error', error);
    }

    showFormError(message, target) {
      if (this.formError) this.formError.textContent = message;
      if (target) {
        target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        target.focus?.({ preventScroll: true });
      }
    }

    clearFormError() {
      if (this.formError) this.formError.textContent = '';
    }

    async validateSubmit(event) {
      if (this.compositesPrepared) {
        this.compositesPrepared = false;
        this.clearFormError();
        return;
      }

      this.clampQuantity();
      this.updateArtMeta('front');
      if (this.isFrontBack()) this.updateArtMeta('back');
      this.updateTextMeta('front');
      if (this.isFrontBack()) this.updateTextMeta('back');
      let error = '';
      let target = null;

      error = core.validateOrder({
        available: Boolean(this.selectedVariant?.available),
        size: this.sizeInput?.value,
        frontFile: this.fileInputs.front?.files?.[0],
        frontText: this.state.text.front.content,
        frontBack: this.isFrontBack(),
        backFile: this.fileInputs.back?.files?.[0],
        backText: this.state.text.back.content,
      });

      if (error.includes('size')) {
        target = this.sizeInput;
      } else if (error.includes('front design')) {
        this.setSide('front');
        target = this.querySelector('[data-gm-content-mode="artwork"]');
      } else if (error.includes('back design')) {
        this.setSide('back');
        target = this.querySelector('[data-gm-content-mode="artwork"]');
      }

      if (error) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showFormError(error, target);
        return;
      }

      this.clearFormError();
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isPreparingSubmit) return;

      this.isPreparingSubmit = true;
      const submitter = event.submitter || this.addButton;
      const originalLabel = this.addButtonText?.textContent || '';
      if (this.addButton) this.addButton.disabled = true;
      if (this.addButtonText) this.addButtonText.textContent = 'PREPARING FINAL DESIGN…';

      try {
        await this.prepareCompositeFiles();
        this.compositesPrepared = true;
        this.isPreparingSubmit = false;
        this.renderAvailability();
        this.renderPrice();
        if (this.form?.requestSubmit) {
          this.form.requestSubmit(submitter || undefined);
        } else {
          this.form?.submit();
        }
        window.setTimeout(() => {
          this.compositesPrepared = false;
        }, 0);
      } catch (preparationError) {
        this.isPreparingSubmit = false;
        this.compositesPrepared = false;
        if (this.addButton) this.addButton.disabled = !this.selectedVariant?.available;
        if (this.addButtonText) this.addButtonText.textContent = originalLabel;
        this.showFormError(
          preparationError?.message || 'The final design could not be prepared. Please try again.',
          this.addButton
        );
      }
    }
  }

  customElements.define(tagName, GroovemanShirtDesigner);
})();
